import React, { type ChangeEvent, type MouseEvent, type ReactNode, type RefObject, useCallback } from 'react';
import { notify } from '@bynder/design-system';
import { IconAnimate, IconLockSmall } from '@bynder/icons';
import { IconComponent } from 'packages/common/icons/IconComponent';
import Editable from '../../../../../editable/Editable';

type Props = {
    name: string;
    displayName: string | ReactNode;
    editable: boolean;
    setEditable: (value: boolean) => void;
    icon: ReactNode;
    hideIcon: ReactNode;
    notEditable?: boolean;
    onAnimationExpand: (e: MouseEvent) => void;
    hasAnimations: boolean;
    isHidden: boolean;
    elementNameNodeRef: RefObject<HTMLDivElement>;
    isLocked: boolean;
    isDirectChildOfDES: boolean;
    onNameChange: (e: ChangeEvent<HTMLInputElement>) => void;
    scrollContainerRef: RefObject<HTMLDivElement>;
    onMountCb: () => void;
};

const Inline = ({
    name,
    displayName,
    editable,
    setEditable,
    icon,
    hideIcon,
    notEditable = false,
    onAnimationExpand,
    hasAnimations,
    isHidden,
    elementNameNodeRef,
    isLocked,
    isDirectChildOfDES,
    onNameChange,
    scrollContainerRef,
    onMountCb = () => {},
}: Props) => {
    const handleConfirm = useCallback(
        (value) => {
            setEditable(false);
            onNameChange(value);
        },
        [onNameChange],
    );
    const stopPropagateHandler = useCallback((e) => e.stopPropagation(), []);

    const showIndicators = (!notEditable && isHidden) || isLocked || hasAnimations || isDirectChildOfDES;

    const handleValidationFail = useCallback((text) => {
        notify({
            id: 'elementNameEditingError',
            title: text,
            variant: 'error',
        });
    }, []);

    return (
        <div className="timeline__element__content h-100">
            <div className="timeline__element__icon">{icon}</div>
            <div className="timeline__element__name">
                <div
                    onKeyDown={(e) => editable && e.nativeEvent.stopImmediatePropagation()}
                    onClick={(e) => editable && e.stopPropagation()}
                >
                    {notEditable ? (
                        name
                    ) : (
                        <Editable
                            valueNodeRef={elementNameNodeRef}
                            key={name}
                            value={name}
                            displayValue={displayName}
                            editable={editable}
                            onToggle={(editable) => setEditable(editable)}
                            onConfirm={(value) => handleConfirm(value)}
                            className="timeline__element__name-editable"
                            confirmBeforeUnmount
                            hideIcon
                            useCapture
                            withDoubleClick
                            validations={{ maxLength: 128 }}
                            ignoreChanges
                            handleValidationFail={handleValidationFail}
                            scrollContainerRef={scrollContainerRef}
                            onMountCb={onMountCb}
                        />
                    )}
                </div>
            </div>
            {showIndicators && (
                <div className="timeline__element__indicators">
                    <div className="timeline__element__indicators__line" onClick={stopPropagateHandler}>
                        {hasAnimations && (
                            <div
                                className="timeline__element__animation"
                                onMouseDown={stopPropagateHandler}
                                onMouseUp={onAnimationExpand}
                            >
                                <IconAnimate size="16" />
                            </div>
                        )}
                        {!notEditable && isHidden && <div className="timeline__element__visibility">{hideIcon}</div>}
                        {isLocked && (
                            <div className="timeline__element__lock">
                                <IconLockSmall size="16" />
                            </div>
                        )}
                        {isDirectChildOfDES && (
                            <div className="timeline__element__des">
                                <IconComponent size="14" />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Inline;
